import type {
  AssetsLibraryItem,
  AssetsLibraryItemState,
} from '../../../../../../music24-api/src/kit/assetsLibrary/assetsLibraryTypes';
import { SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import { SerializedReel } from '../types';
import { assetWorkRow, reelWorkRow, WorkRow } from './studioWorkModel';

/**
 * The rows of the Work page (owner, 2026-10-11). The page reads `assets.list`: the kit asset
 * versions and the AI reels of the artist. Work that makes no file (a lyric sync, a song analysis)
 * is not in that answer, as the owner decided on 2026-10-09. A cover of the cover editor is no
 * generation, so it has no row. Pure, so the rules are tested without React.
 */
export interface WorkEntry {
  key: string;
  uuid: string;
  state: AssetsLibraryItemState;
  catalogItemUuid: string | null;
  item: AssetsLibraryItem;
  /** The step, progress and failure model of the row; null for a reel that has no such model. */
  row: WorkRow | null;
  /** Milliseconds: when the file finished (a ready row), else when it started. */
  at: number;
}

export function workEntry(item: AssetsLibraryItem): WorkEntry | null {
  if (item.kind === 'cover') {
    return null;
  }
  const row =
    item.kind === 'kitAsset'
      ? assetWorkRow(item.asset as unknown as SerializedKitAsset)
      : reelWorkRow(item.reel as unknown as SerializedReel);
  const started = Date.parse(item.at);
  return {
    key: item.key,
    uuid: item.kind === 'kitAsset' ? item.asset.uuid : item.reel.uuid,
    state: item.state,
    catalogItemUuid: item.catalogItemUuid,
    item,
    row,
    at: row?.finishedAt ?? (Number.isNaN(started) ? 0 : started),
  };
}

export function workEntries(items: ReadonlyArray<AssetsLibraryItem> | undefined): WorkEntry[] {
  return (items ?? []).map(workEntry).filter((entry): entry is WorkEntry => entry !== null);
}

/** The queue order: the oldest work first. */
export function byOldest(entries: ReadonlyArray<WorkEntry>): WorkEntry[] {
  return [...entries].sort((a, b) => a.at - b.at);
}

/** The newest first: the failures and the finished files. */
export function byNewest(entries: ReadonlyArray<WorkEntry>): WorkEntry[] {
  return [...entries].sort((a, b) => b.at - a.at);
}

/** True when the uuid is a file of one of the lists (the busy and the failed list of the page). */
export function hasFile(uuid: string, ...lists: ReadonlyArray<ReadonlyArray<WorkEntry>>): boolean {
  return uuid !== '' && lists.some((list) => list.some((entry) => entry.uuid === uuid));
}
